import { useState } from "react";
import { STATUS_STYLES, PRIORITY_STYLES, fmtDate } from "@/lib/constants";
import { stageViews, currentStageLabel } from "@/lib/stages";
import { isUrgent, isOverdue } from "@/lib/batches";
import { StageCards } from "@/components/stages/StageCards";
import { OrderFlowBar } from "@/components/WipPipeline";
import { motion, AnimatePresence } from "framer-motion";
import { Layers, AlertCircle, ChevronDown, ChevronRight, Pencil, History } from "lucide-react";

const SLIDE = { height: { duration: 0.32, ease: [0.4, 0, 0.2, 1] }, opacity: { duration: 0.22 } };
const COLS = 10;
const fmt = (n) => Math.round(Number(n || 0)).toLocaleString("en-IN");

function UrgentIcon({ order }) {
  if (!isUrgent(order)) return null;
  const why = isOverdue(order) ? "Overdue" : order.status === "Delayed" ? "Delayed" : "High priority";
  return <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" title={why} data-testid={`urgent-icon-${order.order_code}`} />;
}

function MemberRow({ o, editable, onEdit, onHistory, onLogStage, onShipStage, onReceiveStage, canManage }) {
  const [open, setOpen] = useState(false);
  const s = STATUS_STYLES[o.status] || STATUS_STYLES.Pending;
  const views = stageViews(o);
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={`batch-member-${o.order_code}`}>
      <div className="px-3 py-2 grid grid-cols-[auto_1fr_auto_auto_auto_auto] items-center gap-3 text-xs">
        <UrgentIcon order={o} />
        <div className="min-w-0">
          <p className="font-mono-data font-semibold text-slate-800">{o.order_code}
            <span className="text-slate-400 font-normal ml-2" data-testid={`batch-member-so-${o.order_code}`}>{o.so_number ? `SO ${o.so_number}` : "No Sales Order"}</span>
          </p>
          <p className="text-[10px] text-slate-400">{currentStageLabel(o)} · {o.line_name || "Unassigned"}</p>
        </div>
        <span className="font-mono-data text-slate-700" data-testid={`batch-member-qty-${o.order_code}`}>{fmt(o.produced_quantity)} / {fmt(o.target_quantity)}</span>
        <span className={`text-[11px] ${isOverdue(o) ? "text-red-600 font-semibold" : "text-slate-500"}`}>{fmtDate(o.due_date)}</span>
        <span className={`text-[10px] font-mono-data uppercase px-1.5 py-0.5 rounded ${PRIORITY_STYLES[o.priority]}`}>{o.priority}</span>
        <div className="flex items-center gap-1">
          <span className={`text-[10px] font-mono-data uppercase px-1.5 py-0.5 rounded ${s.badge}`}>{o.status}</span>
          {onHistory && <button onClick={() => onHistory(o)} className="h-7 w-7 rounded-md flex items-center justify-center text-indigo-600 hover:bg-indigo-100" title="History" data-testid={`batch-member-history-${o.order_code}`}><History className="h-3.5 w-3.5" /></button>}
          {editable && <button onClick={() => onEdit(o)} className="h-7 w-7 rounded-md flex items-center justify-center text-slate-500 hover:bg-blue-100 hover:text-blue-700" title="Edit" data-testid={`batch-member-edit-${o.order_code}`}><Pencil className="h-3.5 w-3.5" /></button>}
          {views.length > 0 && (
            <button onClick={() => setOpen((x) => !x)} className="h-7 px-1.5 rounded-md flex items-center gap-1 text-[11px] text-indigo-600 hover:bg-indigo-100" data-testid={`batch-member-toggle-${o.order_code}`} aria-expanded={open}>
              {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Stages
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="px-3 pb-3 border-t border-slate-100 pt-3">
          <StageCards order={o} canManage={canManage} onLog={onLogStage} onShip={onShipStage} onReceive={onReceiveStage} onHistory={onHistory} />
        </div>
      )}
    </div>
  );
}

export function BatchRow({ batch: b, editable, canManage, expanded, onToggle, onEdit, onHistory, onBatchLog, onLogStage, onShipStage, onReceiveStage }) {
  const s = STATUS_STYLES[b.status] || STATUS_STYLES.Pending;
  const views = stageViews(b);
  const pct = b.target_quantity > 0 ? Math.min(100, Math.round((b.produced_quantity / b.target_quantity) * 100)) : 0;
  const code = b.id;
  return (
    <>
      <tr className={`transition-colors ${s.row}`} data-testid={`batch-row-${b.bom_item_id}`}>
        <td className="px-4 py-3">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-indigo-100 text-indigo-800 px-2 py-1 text-[11px] font-semibold font-mono-data" data-testid={`batch-badge-${b.bom_item_id}`}>
            <Layers className="h-3.5 w-3.5" /> {b.members.length} orders
          </span>
        </td>
        <td className="px-4 py-3 font-medium text-slate-800">
          <span className="flex items-center gap-1.5">
            {b.product_name}
            {b.urgentCount > 0 && <AlertCircle className="h-3.5 w-3.5 text-red-500" title={`${b.urgentCount} urgent / overdue order${b.urgentCount > 1 ? "s" : ""} in this batch`} data-testid={`batch-urgent-${b.bom_item_id}`} />}
          </span>
          <p className="text-[10px] text-slate-400 font-mono-data">{b.members.map((o) => o.order_code).join(" · ")}</p>
        </td>
        <td className="px-4 py-3 text-right font-mono-data text-slate-700" data-testid={`batch-total-qty-${b.bom_item_id}`}>
          {fmt(b.target_quantity)}
          <p className="text-[10px] text-slate-400">Combined</p>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2 min-w-[110px]">
            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
            <span className="text-[11px] font-mono-data text-slate-500">{pct}%</span>
          </div>
          <p className="text-[10px] text-slate-400 font-mono-data mt-0.5">{fmt(b.produced_quantity)} / {fmt(b.target_quantity)} Finished</p>
        </td>
        <td className="px-4 py-3 text-slate-700">{fmtDate(b.due_date)}<p className="text-[10px] text-slate-400">Earliest</p></td>
        <td className="px-4 py-3"><span className={`text-[10px] font-mono-data uppercase tracking-wide px-2 py-0.5 rounded ${PRIORITY_STYLES[b.priority]}`}>{b.priority}</span></td>
        <td className="px-4 py-3 text-xs text-slate-500 font-mono-data">{b.line_names.length ? b.line_names.join(", ") : "Unassigned"}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1 mb-1">
            {views.map((v, i) => <span key={`${v.name}-${i}`} title={`${v.name} · ${fmt(v.done)} / ${fmt(b.target_quantity)}`} className={`h-1.5 w-5 rounded-full ${v.isDone ? "bg-emerald-500" : v.isActive || v.available > 0 ? (v.isOsp ? "bg-orange-500" : "bg-blue-500") : "bg-slate-200"}`} />)}
          </div>
          <p className="text-[10px] text-slate-500 font-mono-data truncate max-w-[150px]">{currentStageLabel(b)}</p>
        </td>
        <td className="px-4 py-3"><span className={`text-[10px] font-mono-data uppercase tracking-wide px-2 py-1 rounded ${s.badge}`}>{b.status}</span></td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-end">
            <button onClick={() => onToggle(code)} className={`h-8 px-2 rounded-md flex items-center gap-1 text-[11px] font-medium transition-colors ${expanded ? "bg-indigo-100 text-indigo-800" : "text-indigo-600 hover:bg-indigo-100"}`} data-testid={`toggle-batch-${b.bom_item_id}`} aria-expanded={expanded}>
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Batch
            </button>
          </div>
        </td>
      </tr>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.tr key="batch-panel" className="bg-slate-50/70" data-testid={`batch-panel-${b.bom_item_id}`}>
            <td colSpan={COLS} className="p-0">
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={SLIDE} className="overflow-hidden">
                <div className="px-6 py-4 space-y-4">
                  <div data-testid={`batch-wip-${b.bom_item_id}`}>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Batch WIP Pipeline · combined quantity flow</p>
                    <OrderFlowBar order={b} />
                  </div>
                  <StageCards order={b} canManage={canManage} onLog={onBatchLog ? (_, idx) => onBatchLog(b, idx) : null} onShip={null} onReceive={null} onHistory={null} batchMode />
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Orders in this batch · {b.members.length}</p>
                    <div className="space-y-1.5" data-testid={`batch-members-${b.bom_item_id}`}>
                      {b.members.map((o) => <MemberRow key={o.id} o={o} editable={editable} canManage={canManage} onEdit={onEdit} onHistory={onHistory} onLogStage={onLogStage} onShipStage={onShipStage} onReceiveStage={onReceiveStage} />)}
                    </div>
                  </div>
                </div>
              </motion.div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
}
